'use strict';

// external imports
import React, {
    MouseEvent,
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {
    Checkbox,
    CheckboxChangeEvent
} from 'primereact/checkbox';
import {confirmPopup} from 'primereact/confirmpopup';
import {Panel} from 'primereact/panel';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';
import {Tooltip} from 'primereact/tooltip';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import Item from '~/shared/src/ci/Item';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {LoadDataParameters} from '~/shared/src/utilities/modules/overview';
import {DataStructure} from '~/shared/src/utilities/dataStructure';

// frontend imports
import {Mode as OverviewDesignerMode} from '~/frontend/src/components/base/OverviewDesigner';
import {ColumnSortMenuColumnSorter} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import ContentButton from '~/frontend/src/components/base/shared/ContentButton';
import StringField from '~/frontend/src/components/base/shared/field/valueTypeFields/StringField';
import OverviewDesignerView from '~/frontend/src/modules/panels/OverviewDesignerView';

import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import {
    getDataRecursive,
    isDataModified,
    isDataModifiedRecursive
} from '~/frontend/src/utilities/dataUtils';
import {
    convertSortersToSortersWithField,
    convertSortersWithFieldToSorters,
    getAllColumns
} from '~/frontend/src/components/base/DataTable/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {setMessage} from '~/frontend/src/actions/intercom';
import origSetParam, {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import deleteOverview from '~/frontend/src/actions/panel/overviewDesigner/delete';
import showDeleteSuccessDialog from '~/frontend/src/actions/panelManager/showDeleteSuccessDialog';


export type DefaultProps = {

    /**
     * The mode of the overviewDesigner
     */
    mode? : OverviewDesignerMode;

    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton? : boolean;

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount? : number;

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip? : React.ReactNode;

    saveButtonDisabled? : boolean;

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton? : boolean;

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton? : boolean;

    deleteButtonDisabled? : boolean;

    /**
     * Show copy XML button.
     */
    showCopyXmlButton? : boolean;

    /**
     * Show download XML button.
     */
    showDownloadXmlButton? : boolean;

    showPanelSelectorDropdown? : boolean;

    /**
     * Show reload button.
     */
    showReloadButton? : boolean;

    /**
     * Show mode selection buttons
     */
    showModeSelector? : boolean;

    showStandaloneSelector? : boolean;
    standalone? : boolean;

    showDataCheckbox? : boolean;
    showData? : boolean;

    showColumnSortButton? : boolean;
    columnDefinitions? : ColumnDefinition[];
    columns? : ColumnConfig[];
    sorters? : ColumnSorter[];

    showHiddenColumnsCheckbox? : boolean;
    showHiddenColumns? : boolean;

    showModifiedHint? : boolean;
};
export const defaultProps : Required<DefaultProps> = {
    mode                     : 'design',
    columnDefinitions        : [],
    columns                  : [],
    saveButtonErrorCount     : 0,
    saveButtonErrorTooltip   : '',
    saveButtonDisabled       : false,
    showCloneButton          : true,
    showColumnSortButton     : true,
    showCopyXmlButton        : true,
    showData                 : false,
    showDataCheckbox         : true,
    showDeleteButton         : true,
    deleteButtonDisabled     : false,
    showDownloadXmlButton    : true,
    showHiddenColumns        : false,
    showHiddenColumnsCheckbox: true,
    showModeSelector         : true,
    showPanelSelectorDropdown: true,
    showReloadButton         : true,
    showSaveButton           : true,
    showStandaloneSelector   : true,
    sorters                  : [],
    standalone               : true,
    showModifiedHint         : false
};

export type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The id of the root item
     */
    rootItemId : number;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    dataContext : string;

    treeViewItem : Item;

    /**
     * Additional parameters for the query.
     *
     * NOTE: A mapping between query fields and contents.
     *
     * USAGE:
     * DataStructure based query:
     *   - key: ${<DataObjectOfQuery.itemKey>.<attributePath>}; value: string
     * Cypher or SQL based query:
     *   - key: ${PlaceholderAsWrittenInQuery}; value: string
     */
    additionalParameters? : Required<LoadDataParameters>[ 'parameters' ];

    /**
     * Event for reload button
     */
    onReloadButtonClick? : () => void;

    onShowDataChange? : ( showData : boolean ) => void;
    onSortersChange? : ( sorters : ColumnSorter[] ) => void;
    onShowHiddenColumnsChange? : ( showHiddenColumns : boolean ) => void;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;

const setParam = origSetParam as <K extends keyof AllProps>( panelKey : string, paramKey : K, paramValue : AllProps[K] ) => SetParamAction;


/**
 * Toolbar
 */
const Toolbar = ( props : AllProps ) => {
    const dispatch : AppDispatch            = useDispatch(),
          {
              additionalParameters,
              columnDefinitions,
              columns,
              dataContext,
              deleteButtonDisabled,
              mode,
              onShowDataChange,
              onShowHiddenColumnsChange,
              onSortersChange,
              rootItemId,
              saveButtonDisabled,
              saveButtonErrorCount,
              saveButtonErrorTooltip,
              schema,
              showCloneButton,
              showColumnSortButton,
              showCopyXmlButton,
              showData,
              showDataCheckbox,
              showDeleteButton,
              showDownloadXmlButton,
              showHiddenColumns,
              showHiddenColumnsCheckbox,
              showModeSelector,
              showModifiedHint,
              showPanelSelectorDropdown,
              showReloadButton,
              showSaveButton,
              showStandaloneSelector,
              sorters,
              standalone,
              treeViewItem,
              uid
          }                                 = props,
          user                              = useSelector( ( state : StoreState ) => state.session?.user ),
          messageCatalogTimestamp           = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          elusiveDataData                   = useSelector( ( state : StoreState ) => uid ? state.elusiveData?.[ uid ]?.data : state.elusiveData?.[ uid ]?.metaData ),
          language                          = user
              ? user.language
              : undefined,
          globalDataVersions                = useMemo(
              () => {
                  return JSON.stringify( elusiveDataData ?? {} );
              },
              [
                  elusiveDataData
              ]
          ),
          data                              = useMemo(
              () => {
                  return getDataRecursive( dataContext, 'CFG', rootItemId );
              },
              [
                  dataContext,
                  globalDataVersions,
                  rootItemId
              ]
          ),
          itemIds                           = useMemo(
              () => {
                  return data.items.map( item => item.id );
              },
              [
                  data
              ]
          ),
          referenceIds                      = useMemo(
              () => {
                  return data.references.map( reference => reference.id );
              },
              [
                  data
              ]
          ),
          allColumnsForSorter               = useMemo(
              () => {
                  return getAllColumns( columnDefinitions, columns, {} as DataStructure, schema, false, false );
              },
              [
                  columnDefinitions,
                  columns
              ]
          ),
          columnSorters                     = useMemo(
              () => {
                  return convertSortersToSortersWithField( sorters );
              },
              [
                  sorters
              ]
          ),
          additionalParametersPanel         = useMemo(
              () => {
                  if ( additionalParameters && Object.keys( additionalParameters ).length ) {
                      const fields : React.ReactNode[] = [];

                      for ( const parameterKey of Object.keys( additionalParameters ) as string[] ) {
                          fields.push(
                              <StringField
                                  key={`${uid}_key`}
                                  uid={`${uid}_key`}
                                  type="text"

                                  label={parameterKey}
                                  labelAlignment="left"
                                  labelPosition="left"

                                  value={additionalParameters[ parameterKey ] ?? undefined}

                                  onChange={( value ) => {
                                      dispatch( setParam( uid, 'additionalParameters', {
                                          ...additionalParameters,
                                          [ parameterKey ]: value ?? ''
                                      } ) );
                                  }}

                                  autoComplete={false}
                                  infoTextLayout="hidden"
                                  markDirty={false}
                                  placeholder={'YN.base.frontend.overviewDesigner.additionalParameters.placeholder'}

                                  style={{ width: '100%' }}
                              />
                          );
                      }

                      return <ContentButton
                          id={`${uid}_contentButton`}

                          horizontalContentAlign="right"
                          verticalContentAlign="bottom"
                          rounded={false}
                          text={true}
                          icon="pi pi-list"
                          label={__( 'YN.base.frontend.overviewDesigner.additionalParameters.button' )}
                          className="yn-option-button yn-view-option-button"
                          contentClassName="yn-contentbutton-overlay-content-page-menu"

                          content={
                              <Panel
                                  className="yn-flex yn-chartdesigner-additionalparameters"
                                  style={{
                                      width: '40rem'
                                  }}
                                  pt={{
                                      content: {
                                          style: {
                                              border: 'none'
                                          }
                                      }
                                  }}
                              >
                                  <div className="fluid formgrid grid" style={{ margin: '0' }}>
                                      <div style={{ width: '100%' }}>
                                          {fields}
                                      </div>
                                  </div>
                              </Panel>
                          }
                      />;
                  }
                  else {
                      return null;
                  }
              },
              [
                  additionalParameters,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          onSaveButtonClick                 = useCallback(
              () => {
                  void OverviewDesignerView.doSaveData( uid, dispatch );
              },
              [
                  uid
              ]
          ),
          onCloneButtonClick                = useCallback(
              ( event : MouseEvent<HTMLButtonElement> ) => {
                  confirmPopup( {
                      target         : event.currentTarget,
                      message        : 'Not implented yet!',
                      className      : 'yn-confirm-popup-notimplemented',
                      icon           : 'pi pi-exclamation-triangle yn-warning',
                      acceptLabel    : __( 'YN.base.common.yes' ),
                      acceptClassName: 'p-button p-button-outlined',
                      rejectLabel    : __( 'YN.base.common.no' ),
                      rejectClassName: 'p-button-secondary p-button-outlined mr-3'
                  } );
              },
              [
                  language
              ]
          ),
          onDeleteButtonClick               = useCallback(
              () => {
                  void dispatch( deleteOverview( uid, rootItemId, () => {
                      dispatch( showDeleteSuccessDialog( uid ) );
                  } ) );
              },
              [
                  uid,
                  rootItemId
              ]
          ),
          onCopyXmlApply                    = useCallback(
              async () => {
                  await navigator.clipboard.writeText( getXmlForContext( dataContext, itemIds, referenceIds, true, true ) );

                  dispatch( setMessage(
                      uid,
                      'toast',
                      'xmlDataCopiedToClipboard',
                      {
                          severity: 'info',
                          summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                          detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                      }
                  ) );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  messageCatalogTimestamp,
                  referenceIds,
                  uid
              ]
          ),
          onDownloadXmlApply                = useCallback(
              () => {
                  fileDownload(
                      getXmlForContext( dataContext, itemIds, referenceIds, true, true ),
                      `ViewDesigner_${treeViewItem.key}`,
                      'xml'
                  );
              },
              [
                  dataContext,
                  language,
                  itemIds,
                  referenceIds,
                  treeViewItem
              ]
          ),
          onReloadButtonClick               = () => {
              props.onReloadButtonClick?.();
          },
          onModeSelectionButtonClick        = useCallback(
              ( modeToSet : AllProps[ 'mode' ] ) => {
                  dispatch( setParam( uid, 'mode', modeToSet ) );
              },
              [
                  uid
              ]
          ),
          onStandaloneSelectionButtonClick  = useCallback(
              ( standaloneToSet : AllProps[ 'standalone' ] ) => {
                  dispatch( setParam( uid, 'standalone', standaloneToSet ) );
              },
              [
                  uid
              ]
          ),
          onShowDataCheckboxChange          = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  onShowDataChange?.( event.checked! );
              },
              [
                  onShowDataChange
              ]
          ),
          onColumnSortChange                = useCallback(
              ( sortersWithField : ColumnSortMenuColumnSorter[] ) => {
                  onSortersChange?.( convertSortersWithFieldToSorters( sortersWithField, columnDefinitions ) );
              },
              [
                  columnDefinitions,
                  onSortersChange
              ]
          ),
          onShowHiddenColumnsCheckboxChange = useCallback(
              ( event : CheckboxChangeEvent ) => {
                  onShowHiddenColumnsChange?.( event.checked! );
              },
              [
                  onShowHiddenColumnsChange
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            if ( showSaveButton ) {
                layoutComponents.left.push( {
                    type  : 'saveButton',
                    saveButtonErrorCount,
                    saveButtonErrorTooltip,
                    params: {
                        key: `${uid}_saveButton`,

                        disabled: saveButtonDisabled,
                        onClick : onSaveButtonClick
                    }
                } );
            }

            if ( showCloneButton ) {
                layoutComponents.left.push( {
                    type  : 'cloneButton',
                    params: {
                        key: `${uid}_cloneButton`,

                        onClick: onCloneButtonClick
                    }
                } );
            }

            if ( showDeleteButton ) {
                layoutComponents.left.push( {
                    type  : 'deleteButton',
                    params: {
                        key: `${uid}_deleteButton`,

                        disabled: deleteButtonDisabled,
                        confirm : true,
                        onClick : onDeleteButtonClick
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showCopyXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'copyXml',
                    params: {
                        key: `${uid}_copyXmlButton`,

                        uid,

                        onExpand: () => {
                            void onCopyXmlApply();
                            return false;
                        },
                        onApply : () => {
                            void onCopyXmlApply();
                        }
                    }
                } );
            }

            if ( showDownloadXmlButton ) {
                layoutComponents.left.push( {
                    type  : 'downloadXml',
                    params: {
                        key: `${uid}_downloadXmlButton`,

                        uid,

                        onExpand: () => {
                            onDownloadXmlApply();
                            return false;
                        },
                        onApply : onDownloadXmlApply
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModeSelector ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_modeSelectionButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_designModeButton`,

                                    label    : __( 'YN.base.frontend.overviewDesigner.mode.design.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'design' ),
                                    className: mode === 'design' ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_inputPanelButton`,

                                    label    : __( 'YN.base.frontend.overviewDesigner.mode.input.label' ),
                                    onClick  : onModeSelectionButtonClick.bind( null, 'input' ),
                                    className: mode === 'input' ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showStandaloneSelector ) {
                layoutComponents.left.push( {
                    type  : 'buttonSet',
                    params: {
                        key: `${uid}_standaloneSelectionButtons`,

                        buttons: [
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_standaloneButton`,

                                    label    : __( 'YN.base.frontend.overviewDesigner.standalone.true.label' ),
                                    onClick  : onStandaloneSelectionButtonClick.bind( null, true ),
                                    className: standalone ? '' : 'p-button-outlined'
                                }
                            },
                            {
                                type  : 'button',
                                params: {
                                    key: `${uid}_embeddedButton`,

                                    label    : __( 'YN.base.frontend.overviewDesigner.standalone.false.label' ),
                                    onClick  : onStandaloneSelectionButtonClick.bind( null, false ),
                                    className: !standalone ? '' : 'p-button-outlined'
                                }
                            }
                        ]
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showDataCheckbox ) {
                if ( additionalParametersPanel ) {
                    const hasEmptyParameter = Boolean( additionalParameters && Object.values( additionalParameters ).includes( '' ) );

                    layoutComponents.left.push( {
                        type: 'genericNode',

                        params: <div
                            key={`${uid}_showDataContainer`}
                            id={`${uid}_showDataContainer`}
                            className={`yn-toolbar-container ${hasEmptyParameter ? 'yn-toolbar-container-error' : ''}`}
                        >
                            {additionalParametersPanel}
                            <div key={`${uid}_showDataCheckbox`} className="flex align-items-center field ml-2">
                                <label
                                    htmlFor={`${uid}_showDataCheckbox`}
                                    className="block"
                                >
                                    {__( 'YN.base.frontend.overviewDesigner.showData.label' )}
                                </label>
                                <Checkbox
                                    checked={!hasEmptyParameter && showData}
                                    disabled={hasEmptyParameter}
                                    onChange={onShowDataCheckboxChange}
                                    style={{ flex: '0 0 auto', width: 'auto' }}
                                />
                            </div>
                            {hasEmptyParameter &&
                                <Tooltip
                                    key="showDataContainerTooltip"
                                    target={`#${uid}_showDataContainer`}
                                    my="left top"
                                    at="left bottom"
                                    className="yn-tooltip"
                                    showDelay={500}
                                >
                                    {__( 'YN.base.frontend.overviewDesigner.additionalParameters.error.tooltip' )}
                                </Tooltip>}
                        </div>
                    } );
                }
                else {
                    layoutComponents.left.push( {
                        type: 'genericNode',

                        params: <div
                            key={`${uid}_showDataCheckbox`}
                            className="flex align-items-center field"
                        >
                            <label
                                htmlFor={`${uid}_showDataCheckbox`}
                                className="block"
                            >
                                {__( 'YN.base.frontend.overviewDesigner.showData.label' )}
                            </label>
                            <Checkbox
                                checked={showData}
                                onChange={onShowDataCheckboxChange}
                                style={{ flex: '0 0 auto', width: 'auto' }}
                            />
                        </div>
                    } );
                }
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showColumnSortButton ) {
                layoutComponents.left.push( {
                    type  : 'columnSortMenu',
                    params: {
                        key: `${uid}_columnsort`,

                        uid: uid,

                        labeled: true,

                        columns : allColumnsForSorter,
                        sorters : columnSorters,
                        onChange: onColumnSortChange
                    }
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showHiddenColumnsCheckbox ) {
                layoutComponents.left.push( {
                    type: 'genericNode',

                    params: <div key={`${uid}_showHiddenColumnsCheckbox`} className="flex align-items-center field">
                        <label
                            htmlFor={`${uid}_showHiddenColumnsCheckbox`}
                            className="block"
                        >
                            {__( 'YN.base.frontend.overviewDesigner.showHiddenColumns.label' )}
                        </label>
                        <Checkbox
                            checked={showHiddenColumns}
                            onChange={onShowHiddenColumnsCheckboxChange}
                            style={{ flex: '0 0 auto', width: 'auto' }}
                        />
                    </div>
                } );
            }

            layoutComponents.left.push( {
                type: 'separator'
            } );

            if ( showModifiedHint ) {
                layoutComponents.left.push( {
                    type  : 'custom',
                    params: {
                        key      : `${uid}_modifiedHint`,
                        className: 'flex align-items-center',
                        component: <>
                            <div className="yn-toolbar-modified-hint-icon"/>
                            <span className="ml-2 yn-secondary">{__( 'YN.base.frontend.common.modified.hint' )}</span>
                        </>
                    }
                } );
            }


            // right
            if ( showPanelSelectorDropdown ) {
                const item       = rootItemId
                          ? global.data[ dataContext ]?.items[ rootItemId ]
                          : undefined,
                      itemParams = item
                          ? {
                              schema  : item.schema,
                              itemId  : item.id,
                              itemType: item.itemTypeId
                          }
                          : {};

                layoutComponents.right.push( {
                    type  : 'panelSelectorDropdown',
                    params: {
                        key: `${uid}_panelSelector`,

                        uid        : `${uid}_panelSelector`,
                        labeled    : true,
                        style      : { width: '18rem' },
                        currentView: `OverviewDesignerView`,
                        ...itemParams
                    }
                } );
            }

            layoutComponents.right.push( {
                type: 'separator'
            } );

            if ( showReloadButton ) {
                layoutComponents.right.push( {
                    type  : 'reloadButton',
                    params: {
                        key: `${uid}_reloadButton`,

                        confirm: () => rootItemId ? isDataModifiedRecursive( dataContext, schema, rootItemId ) : isDataModified( dataContext, schema! ),
                        onClick: onReloadButtonClick
                    }
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />;
        },
        [
            additionalParameters,
            additionalParametersPanel,
            dataContext,
            language,
            messageCatalogTimestamp,
            mode,
            onCloneButtonClick,
            onColumnSortChange,
            onCopyXmlApply,
            onDeleteButtonClick,
            onDownloadXmlApply,
            onModeSelectionButtonClick,
            onSaveButtonClick,
            onShowDataCheckboxChange,
            onShowHiddenColumnsCheckboxChange,
            rootItemId,
            saveButtonErrorCount,
            saveButtonErrorTooltip,
            showCloneButton,
            showCopyXmlButton,
            showData,
            showDeleteButton,
            showDownloadXmlButton,
            showHiddenColumns,
            showHiddenColumnsCheckbox,
            showModeSelector,
            showModifiedHint,
            showReloadButton,
            showSaveButton,
            uid
        ]
    );
};
Toolbar.defaultProps = defaultProps;
Toolbar.displayName = 'OverviewDesigner-Toolbar';

export default Toolbar;
